import { AttachFile, Image, InsertDriveFile } from '@mui/icons-material';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttachmentMode } from './message-input.types';

interface AttachmentTypeMenuProps {
  onSelectMedia: () => void;
  onSelectFiles: () => void;
  disabled?: boolean;
  activeMode: AttachmentMode;
}

/**
 * Split menu for file attachment - allows choosing Media or Files mode.
 * Once a mode is selected and files attached, other mode is locked until cleared.
 */
export const AttachmentTypeMenu = ({
  onSelectMedia,
  onSelectFiles,
  disabled = false,
  activeMode
}: AttachmentTypeMenuProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectMedia = () => {
    onSelectMedia();
    handleClose();
  };

  const handleSelectFiles = () => {
    onSelectFiles();
    handleClose();
  };

  // Determine which options are disabled based on active mode
  const isMediaDisabled = disabled || activeMode === 'files';
  const isFilesDisabled = disabled || activeMode === 'media';

  return (
    <>
      <IconButton
        size='small'
        onClick={handleClick}
        disabled={disabled}
        aria-label={t('messages.attachmentMenu.attach')}
        aria-controls={open ? 'attachment-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        sx={{
          color: activeMode ? 'primary.main' : 'inherit'
        }}
      >
        <AttachFile />
      </IconButton>
      <Menu
        id='attachment-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MenuItem onClick={handleSelectMedia} disabled={isMediaDisabled}>
          <ListItemIcon>
            <Image fontSize='small' color={activeMode === 'media' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText
            primary={t('messages.attachmentMenu.media')}
            secondary={t('messages.attachmentMenu.mediaDescription')}
          />
        </MenuItem>
        <MenuItem onClick={handleSelectFiles} disabled={isFilesDisabled}>
          <ListItemIcon>
            <InsertDriveFile fontSize='small' color={activeMode === 'files' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText
            primary={t('messages.attachmentMenu.files')}
            secondary={t('messages.attachmentMenu.filesDescription')}
          />
        </MenuItem>
      </Menu>
    </>
  );
};
